import React, { useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Stack, Chip, CircularProgress, Alert,
  MenuItem, Divider,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

const eur = (n) => `€ ${(Number(n) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AchmeaExport() {
  const [number, setNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);      // { header, lines, codes, omvang, subject, staffel, verify }
  const [busy, setBusy] = useState(false);
  const [showTarieven, setShowTarieven] = useState(false);

  const load = async () => {
    if (!number.trim()) return;
    setLoading(true); setError(null); setData(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/achmea-export/invoice?number=${encodeURIComponent(number.trim())}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Ophalen mislukt');
      setData(d);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const setHeader = (k, v) => setData((d) => ({ ...d, header: { ...d.header, [k]: v } }));
  const setLineCode = (idx, code) => setData((d) => {
    const codes = d.codes.find((c) => c.code === code);
    const lines = d.lines.map((l) => l.idx === idx ? {
      ...l, code,
      bedragNetto: code === 'O01' ? l.sellingprice : (codes?.tarief || 0),
    } : l);
    return { ...d, lines };
  });

  const download = async () => {
    setBusy(true); setError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/achmea-export/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ header: data.header, lines: data.lines, staffel: data.staffel, projectNumber: number.trim() }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || 'Genereren mislukt'); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Achmea_${data.header.factuurnummer || 'export'}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  // Totaal met de échte korting per regel (Gripp line-discount; 0% blijft 0%).
  const lineStaffel = (l) => (l.discount === null || l.discount === undefined) ? (data?.staffel || 0) : (Number(l.discount) || 0);
  const totExcl = data ? data.lines.reduce((s, l) => {
    const rate = l.code === 'O01' ? l.sellingprice : (data.codes.find((c) => c.code === l.code)?.tarief || 0);
    return s + (l.aantal * rate) * (1 - lineStaffel(l) / 100);
  }, 0) : 0;
  const grippExcl = data?.verify?.grippExclBtw || 0;
  const totKlopt = data ? Math.abs(totExcl - grippExcl) <= 0.02 : true;

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
        <DescriptionIcon sx={{ color: '#0f766e' }} />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Achmea / Interpolis factuur-export</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Haal een Gripp-factuur op (projectnummer), controleer per regel de Achmea-tariefcode en download de Excel.
        Correcties worden onthouden voor de volgende keer.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField size="small" label="Projectnummer (bv. 9390)" value={number}
            onChange={(e) => setNumber(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} sx={{ width: 240 }} />
          <Button variant="contained" onClick={load} disabled={loading}
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 700 }}>Ophalen</Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {data.subject} {data.omvang && <Chip size="small" label={`Omvang ${data.omvang}`} sx={{ ml: 1, height: 18, fontSize: 10 }} />}
          </Typography>

          {/* 📄 Laatste gedownloade export — herstelbaar (die versie was waarschijnlijk de goede) */}
          {data.lastExport?.payload && (
            <Alert severity="info" sx={{ mb: 2 }}
              action={
                <Button size="small" color="inherit" sx={{ fontWeight: 700, textTransform: 'none' }}
                  onClick={() => setData((d) => ({
                    ...d,
                    header: { ...d.header, ...(data.lastExport.payload.header || {}) },
                    lines: Array.isArray(data.lastExport.payload.lines) && data.lastExport.payload.lines.length
                      ? data.lastExport.payload.lines : d.lines,
                  }))}>
                  Herstel die versie
                </Button>
              }>
              Laatste gedownloade Excel: {data.lastExport.updated_at ? new Date(data.lastExport.updated_at).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
              {data.lastExport.updated_by ? ` door ${String(data.lastExport.updated_by).split('@')[0]}` : ''} — inclusief handmatige correcties.
            </Alert>
          )}

          {/* ✅ Verificatie: klopt de Excel met de Gripp-factuur (en de offerte)? */}
          {data.verify && (
            <Alert severity={totKlopt ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {totKlopt ? (
                <>Excel-totaal <b>{eur(totExcl)}</b> komt overeen met de Gripp-factuur (excl. BTW, na korting {eur(data.verify.grippExclBtw)}).</>
              ) : (
                <>
                  <b>Let op:</b> Excel komt op <b>{eur(totExcl)}</b>, de Gripp-factuur op <b>{eur(data.verify.grippExclBtw)}</b> excl. BTW
                  {' '}(verschil {eur(totExcl - grippExcl)}).
                  {(data.verify.perLine || []).length > 0 && (
                    <Box component="ul" sx={{ m: 0.5, pl: 2.5 }}>
                      {data.verify.perLine.map((p, i) => (
                        <li key={i}><Typography variant="caption"><b>{p.product}</b>: Excel {eur(p.excel)} vs Gripp {eur(p.gripp)} — {p.reden}</Typography></li>
                      ))}
                    </Box>
                  )}
                  {(data.verify.skipped || []).length > 0 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      Niet meegenomen regels uit Gripp: {data.verify.skipped.map((s) => `${s.product} (${eur(s.bedrag)})`).join(', ')}
                    </Typography>
                  )}
                </>
              )}
            </Alert>
          )}
          {data.verify?.offerte && (
            <Alert severity={Math.abs(data.verify.offerte.verschilMetFactuur) <= 0.02 ? 'success' : 'info'} sx={{ mb: 2 }}>
              {Math.abs(data.verify.offerte.verschilMetFactuur) <= 0.02 ? (
                <>Factuur komt overeen met offerte {data.verify.offerte.nummer} ({eur(data.verify.offerte.totaalExclBtw)} excl. BTW).</>
              ) : (
                <>Offerte {data.verify.offerte.nummer} was {eur(data.verify.offerte.totaalExclBtw)} excl. BTW — factuur wijkt{' '}
                <b>{eur(data.verify.offerte.verschilMetFactuur)}</b> af (meer-/minderwerk? Check bewust.)</>
              )}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 1 }}>Factuurgegevens (controleer + vul aan)</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
              <TextField size="small" label="Factuurnummer (bv. 20260245)" placeholder="20260245"
                value={data.header.factuurnummer} onChange={(e) => setHeader('factuurnummer', e.target.value)}
                helperText={!data.header.factuurnummer ? 'Concept-factuur heeft nog geen nummer — vul het definitieve factuurnummer in' : undefined} />
              <TextField size="small" label="Datum (YYYY-MM-DD)" value={data.header.datum} onChange={(e) => setHeader('datum', e.target.value)} />
              <TextField size="small" label="Kostenplaats / WBS / Ariba" value={data.header.kostenplaats} onChange={(e) => setHeader('kostenplaats', e.target.value)} />
              <TextField size="small" label="Budget houder" value={data.header.budgethouder} onChange={(e) => setHeader('budgethouder', e.target.value)} />
              <TextField size="small" label="Bedrijfsonderdeel" value={data.header.bedrijfsonderdeel} onChange={(e) => setHeader('bedrijfsonderdeel', e.target.value)} />
              <TextField size="small" label="Type opdracht (bv. Smartvideo)" value={data.header.typeOpdracht} onChange={(e) => setHeader('typeOpdracht', e.target.value)} />
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, overflowX: 'auto' }}>
            <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 1 }}>
              Regels — controleer de tariefcode (rood = vangnet/tarief-match, check!)
            </Typography>
            <Stack spacing={0.75} sx={{ minWidth: 720 }}>
              {data.lines.map((l) => {
                const rate = l.code === 'O01' ? l.sellingprice : (data.codes.find((c) => c.code === l.code)?.tarief || 0);
                const staf = lineStaffel(l);
                const totNetto = l.aantal * rate;
                const rateMismatch = l.code !== 'O01' && Math.abs(rate - l.sellingprice) > 0.01;
                const mismatch = l.source === 'vangnet' || rateMismatch;
                return (
                  <Stack key={l.idx} direction="row" spacing={1} alignItems="center" sx={{ fontSize: 13 }}>
                    <Box sx={{ flex: 2, overflow: 'hidden' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>{l.product}</Typography>
                      {l.description && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.description}</Typography>}
                      {rateMismatch && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#b45309', fontWeight: 600 }}>
                          ⚠️ Gripp-prijs {eur(l.sellingprice)} ≠ Achmea-tarief {eur(rate)} van {l.code}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" sx={{ width: 60, textAlign: 'right' }}>{l.aantal} {l.unit}</Typography>
                    <Typography variant="caption" sx={{ width: 80, textAlign: 'right', color: '#64748b' }}>{eur(l.sellingprice)}</Typography>
                    <Typography variant="caption" sx={{ width: 55, textAlign: 'right', color: staf === 0 ? '#b45309' : '#64748b', fontWeight: staf === 0 ? 700 : 400 }}>
                      {staf}%
                    </Typography>
                    <TextField select size="small" value={l.code} onChange={(e) => setLineCode(l.idx, e.target.value)}
                      sx={{ width: 200, '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
                      error={mismatch}
                      SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 360 } } } }}>
                      {data.codes.map((c) => <MenuItem key={c.code} value={c.code} sx={{ fontSize: 12 }}>{c.label}</MenuItem>)}
                    </TextField>
                    <Typography variant="caption" sx={{ width: 90, textAlign: 'right', fontWeight: 700 }}>{eur(totNetto)}</Typography>
                  </Stack>
                );
              })}
            </Stack>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', textAlign: 'right' }}>
              Totaal Excl BTW (korting per regel uit Gripp): {eur(totExcl)}
              {data.verify && !totKlopt && <Box component="span" sx={{ color: '#b45309' }}> · Gripp-factuur: {eur(grippExcl)}</Box>}
            </Typography>
          </Paper>

          <Button variant="contained" size="large" onClick={download} disabled={busy}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d5f58' } }}>
            Download Achmea-Excel
          </Button>

          {/* Tarievenkaart — ter referentie; aanpassen kan in Admin → Achmea tarieven */}
          <Paper variant="outlined" sx={{ p: 1.5, mt: 2.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} onClick={() => setShowTarieven((v) => !v)} sx={{ cursor: 'pointer' }}>
              <Typography variant="caption" sx={{ fontWeight: 800 }}>
                {showTarieven ? '▾' : '▸'} Achmea-tarievenkaart ({data.codes.length} codes)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                — aanpassen kan in <a href="/admin?sectie=achmea-tarieven" style={{ color: '#0f766e' }}>Admin → Achmea tarieven</a>
              </Typography>
            </Stack>
            {showTarieven && (
              <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 0.25 }}>
                {data.codes.map((c) => (
                  <Typography key={c.code} variant="caption" sx={{ color: '#475569' }}>
                    <b>{c.code}</b> {c.label.split('— ')[1] || ''} — {c.tarief > 0 ? eur(c.tarief) : 'n.v.t.'}
                  </Typography>
                ))}
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}
